import React, { useState } from 'react';
import { Upload, Modal, Image, Button } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import {
  DeleteOutlined,
  PictureOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import {
  uploadSingleFile,
  deleteSingleFile,
  uploadOriginalSingleFile,
  uploadSingleFileWithoutResize,
  uploadSingleFileWithOriginalExtensionResize,
} from 'modules/galleryModules/requests/galleryRequests';
import uploadImageOptions from 'modules/layoutModule/helpers/uploadImageOptions';
import { StaticImagesFallbackUrl } from 'modules/layoutModule/consts/StaticImagesFallbackUrl';
import staticUrl from 'helpers/staticUrl';
import copyToClipboard from 'helpers/copyToClipboard';

export interface IFormCoverUploadPropTypes {
  cover: string | null;
  setCover: (arg0: any) => void;
  isNew?: boolean;
  field: string;
  id?: string;
  number?: number;
  hideTags?: boolean;
  originalFile?: boolean;
  dontResize?: boolean;
  asPng?: boolean;
}

const FormCoverUpload: React.FC<IFormCoverUploadPropTypes> = ({
  cover,
  setCover,
  isNew,
  field,
  id,
  number,
  hideTags,
  originalFile,
  dontResize,
  asPng,
}): JSX.Element => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewLoading, toggleLoading] = useState(false);

  const handleChangeImage = (fileId: UploadChangeParam) => {
    toggleLoading(true);
    if (fileId && fileId.file.status === 'removed') {
      setCover(null);
    }
  };

  const handlePreview = async (file: UploadFile) => {
    setPreviewVisible(true);
    setPreviewImage(
      `${staticUrl()}${isNew ? 'upload/' : ''}${file.name}_medium.jpg`
    );
  };

  const customRequest = (option: { file: string | Blob }) => {
    const formData = new FormData();
    formData.append('photo', option.file);
    if (asPng) {
      return uploadSingleFileWithOriginalExtensionResize(formData).then(
        (response) => {
          if (response) {
            const { data } = response;
            setCover(data.name);
            toggleLoading(false);
          }
        }
      );
    }
    if (dontResize) {
      return uploadSingleFileWithoutResize(formData).then((response) => {
        if (response) {
          const { data } = response;
          setCover(data.name);
          toggleLoading(false);
        }
      });
    }
    if (originalFile) {
      uploadOriginalSingleFile(formData).then((response) => {
        if (response) {
          const { data } = response;
          setCover(data.thumbUrl);
          toggleLoading(false);
        }
      });
    } else {
      uploadSingleFile(formData).then((response) => {
        if (response) {
          const { data } = response;
          setCover(data.name);
          toggleLoading(false);
        }
      });
    }
  };

  const handleCancel = () => setPreviewVisible(false);

  const imageUrl = () => {
    if (!cover) {
      return 'error';
    }
    if (asPng) {
      return `${staticUrl()}${isNew ? 'upload/' : ''}${cover}_big.png`;
    }
    if (originalFile) {
      if (cover.includes('upload/')) {
        return cover;
      }
      return `${staticUrl()}${isNew ? 'upload/' : ''}${cover}`;
    }
    return `${staticUrl()}${isNew ? 'upload/' : ''}${cover}_big.jpg`;
  };

  const imagePreviewUrl = () => {
    if (!cover) {
      return false;
    }
    if (asPng) {
      return {
        src: `${staticUrl()}${isNew ? 'upload/' : ''}${cover}_big.png`,
      };
    }
    if (originalFile) {
      if (cover.includes('upload/')) {
        return { src: cover };
      }
      return { src: `${staticUrl()}${isNew ? 'upload/' : ''}${cover}` };
    }
    return {
      src: `${staticUrl()}${isNew ? 'upload/' : ''}${cover}_big.jpg`,
    };
  };

  const deleteCover = () =>
    deleteSingleFile({
      cover,
      id,
      field,
    }).then(() => {
      setCover(null);
      toggleLoading(false);
    });

  if (cover) {
    return (
      <div className="image-upload-row">
        <div>
          <Button
            icon={<DeleteOutlined />}
            onClick={deleteCover}
            className="image-preview-button"
          >
            Usuń plik
          </Button>
          <div className="image-preview">
            <Image
              width={194}
              src={imageUrl()}
              preview={imagePreviewUrl()}
              fallback={StaticImagesFallbackUrl}
            />
          </div>
        </div>
        {!hideTags && (
          <div className="image-upload-row-tags">
            <Button
              type="link"
              onClick={() => copyToClipboard(`IMGR#${number || 1}#50`)}
              style={{ padding: 0, margin: 0 }}
            >
              IMGR#{number || 1}#50
            </Button>
            <Button
              type="link"
              onClick={() => copyToClipboard(`IMGR#${number || 1}#100`)}
              style={{ padding: 0, margin: 0 }}
            >
              IMGR#{number || 1}#100
            </Button>
            <Button
              type="link"
              onClick={() => copyToClipboard(`IMGL#${number || 1}#50`)}
              style={{ padding: 0, margin: 0 }}
            >
              IMGL#{number || 1}#50
            </Button>
            <Button
              type="link"
              onClick={() => copyToClipboard(`IMGL#${number || 1}#100`)}
              style={{ padding: 0, margin: 0 }}
            >
              IMGL#{number || 1}#100
            </Button>
            <Button
              type="link"
              onClick={() => copyToClipboard(`IMGC#${number || 1}#50`)}
              style={{ padding: 0, margin: 0 }}
            >
              IMGC#{number || 1}#50
            </Button>
            <Button
              type="link"
              onClick={() => copyToClipboard(`IMGC#${number || 1}#100`)}
              style={{ padding: 0, margin: 0 }}
            >
              IMGC#{number || 1}#100
            </Button>
          </div>
        )}
      </div>
    );
  }

  const moreOptions = () => {
    if (asPng) {
      return {
        accept: '.png',
      };
    }
  };

  return (
    <>
      <Upload
        {...uploadImageOptions}
        {...moreOptions()}
        listType="picture-card"
        showUploadList={false}
        onPreview={handlePreview}
        onChange={handleChangeImage}
        customRequest={customRequest}
        disabled={previewLoading}
      >
        {previewLoading ? <LoadingOutlined /> : <PictureOutlined />}
      </Upload>
      <Modal
        visible={previewVisible}
        title="Podgląd"
        footer={null}
        onCancel={handleCancel}
      >
        <img
          alt=""
          style={{
            width: '100%',
          }}
          src={previewImage || ''}
        />
      </Modal>
    </>
  );
};

export default FormCoverUpload;
